import { useEffect, useMemo, useState } from 'react';
import type { StepProps } from '../../OBWWizard';
import { listPartnerCandidates, launchPow, type PartnerCandidate } from '../../../lib/beta-sites-api';

// ADR-076 Option 2 — the subscriber picker. FIRST step of the STAFF partner-apply
// fork only (the subscriber self-serve journey is unchanged). A partner IS a
// subscriber-who-resells, so the consultant picks an EXISTING subscriber to apply
// on behalf of. On Continue it mints the admin-assisted POW launch token (keyed
// to the picked subscriber) and seeds it forward; the journey can NEVER advance
// without a customerNumber. The org-identity cell (org + CN stacked) is LOCAL to
// this modal — not a platform pattern.

type SortKey = 'businessName' | 'contactName' | 'email' | 'status';
type FilterMode = 'eligible' | 'all' | 'partners';

export function PartnerPickSubscriberStep({ onNext, requestExit }: StepProps) {
  const [rows, setRows] = useState<PartnerCandidate[]>([]);
  const [loadState, setLoadState] = useState<'loading' | 'loaded' | 'error'>('loading');
  const [search, setSearch] = useState('');
  const [filter, setFilter] = useState<FilterMode>('eligible');
  const [sort, setSort] = useState<{ key: SortKey; dir: 'asc' | 'desc' }>({ key: 'businessName', dir: 'asc' });
  const [selected, setSelected] = useState<PartnerCandidate | null>(null);
  const [launching, setLaunching] = useState(false);
  const [launchError, setLaunchError] = useState('');
  // ADR-077 A1.3 — continue/restart fork for a picked subscriber who already has
  // an in-progress application. `fork` = the picked candidate (show the choice);
  // `ack` = "Continue existing" chosen (show the queue pointer + Close).
  const [fork, setFork] = useState<PartnerCandidate | null>(null);
  const [ack, setAck] = useState(false);

  useEffect(() => {
    let cancelled = false;
    void (async () => {
      try {
        const r = await listPartnerCandidates();
        if (!cancelled) { setRows(r.items); setLoadState('loaded'); }
      } catch {
        if (!cancelled) setLoadState('error');
      }
    })();
    return () => { cancelled = true; };
  }, []);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    const list = rows.filter((r) => {
      if (filter === 'eligible' && r.isPartner) return false;
      if (filter === 'partners' && !r.isPartner) return false;
      if (q) {
        const hay = `${r.businessName} ${r.contactName} ${r.email} ${r.customerNumber}`.toLowerCase();
        if (!hay.includes(q)) return false;
      }
      return true;
    });
    const factor = sort.dir === 'asc' ? 1 : -1;
    return [...list].sort((a, b) => {
      const av = sort.key === 'status' ? (a.isPartner ? 1 : 0) : (a[sort.key] ?? '').toLowerCase();
      const bv = sort.key === 'status' ? (b.isPartner ? 1 : 0) : (b[sort.key] ?? '').toLowerCase();
      return av < bv ? -1 * factor : av > bv ? 1 * factor : 0;
    });
  }, [rows, search, filter, sort]);

  const toggleSort = (key: SortKey) =>
    setSort((s) => (s.key === key ? { key, dir: s.dir === 'asc' ? 'desc' : 'asc' } : { key, dir: 'asc' }));
  const ariaSort = (key: SortKey): 'ascending' | 'descending' | 'none' =>
    sort.key === key ? (sort.dir === 'asc' ? 'ascending' : 'descending') : 'none';

  // Mint the launch token for the picked subscriber and advance into the apply
  // journey. `supersedeApplicationId` (set only on the "Start fresh" fork path)
  // flows to pa-submit so /portal/pow/provision supersedes the in-progress Brief.
  async function launchAndProceed(candidate: PartnerCandidate, supersedeApplicationId?: string) {
    setLaunching(true);
    setLaunchError('');
    try {
      const r = await launchPow(candidate.accountId);
      onNext({
        powToken: r.token,
        customerNumber: r.prefill.customerNumber,
        email: r.prefill.email,
        businessName: r.prefill.businessName,
        websiteUrl: r.prefill.websiteUrl,
        pickedOrg: candidate.businessName || r.prefill.businessName,
        pickedCustomerNumber: candidate.customerNumber,
        ...(supersedeApplicationId ? { supersedeApplicationId } : {}),
      });
    } catch {
      setLaunchError('Could not start the application for this subscriber. Please try again.');
      setLaunching(false);
    }
  }

  async function handleContinue() {
    if (!selected || selected.isPartner) return;
    // ADR-077 A1.3 — a picked subscriber with an in-progress application gets the
    // continue/restart fork before any launch; otherwise launch straight in.
    if (selected.hasInProgressApplication) {
      setFork(selected);
      return;
    }
    await launchAndProceed(selected);
  }

  // ADR-077 A1.3 — "Continue existing" was chosen: the staff operator has no
  // subscriber session to resume the wizard, so the existing application is
  // tracked in the Partners queue. Acknowledge + close (no Brief is touched).
  if (ack && fork) {
    const org = fork.businessName || fork.customerNumber;
    return (
      <div className="obw-step-content">
        <h2 className="obw-title">Application already in progress</h2>
        <p className="obw-subtitle">
          <strong>{org}</strong> already has a partner application in progress. It’s been left as is —
          track and action it from the <strong>Partners queue</strong>.
        </p>
        <div className="portal-wizard-nav" style={{ marginTop: '24px' }}>
          <button type="button" className="obw-btn-primary" onClick={() => requestExit?.()}>
            Close
          </button>
        </div>
      </div>
    );
  }

  // ADR-077 A1.3 — the continue/restart fork for the picked in-progress subscriber.
  if (fork) {
    const org = fork.businessName || fork.customerNumber;
    return (
      <div className="obw-step-content">
        <h2 className="obw-title">This subscriber has an application in progress</h2>
        <p className="obw-subtitle">
          <strong>{org}</strong> already has a partner application in progress. Continue with the
          existing one, or start a fresh application — starting fresh replaces the one in progress
          (it’s kept on record, no longer active).
        </p>
        {launchError && <div className="portal-wizard-error" role="alert">{launchError}</div>}
        <div style={{ display: 'flex', flexDirection: 'column', gap: '12px', marginTop: '20px' }}>
          <button
            type="button"
            className="obw-btn-primary"
            onClick={() => setAck(true)}
            style={{ width: '100%', textAlign: 'left', padding: '16px 18px' }}
          >
            <span style={{ display: 'block', fontWeight: 600 }}>Continue the existing application</span>
            <span style={{ display: 'block', fontSize: '14px', opacity: 0.85, marginTop: '4px' }}>
              Leave it as is — track it in the Partners queue.
            </span>
          </button>
          <button
            type="button"
            className="obw-btn-secondary"
            onClick={() => void launchAndProceed(fork, fork.inProgressApplicationId ?? undefined)}
            disabled={launching}
            style={{ width: '100%', textAlign: 'left', padding: '16px 18px' }}
          >
            <span style={{ display: 'block', fontWeight: 600 }}>{launching ? 'Starting…' : 'Start a fresh application'}</span>
            <span style={{ display: 'block', fontSize: '14px', opacity: 0.85, marginTop: '4px' }}>
              Begin again; the in-progress application is replaced (kept on record).
            </span>
          </button>
        </div>
        <div className="portal-wizard-nav" style={{ marginTop: '16px' }}>
          <button type="button" className="obw-btn-secondary" onClick={() => setFork(null)} disabled={launching}>
            ← Back to subscribers
          </button>
        </div>
      </div>
    );
  }

  const cols: Array<{ key: SortKey; label: string }> = [
    { key: 'businessName', label: 'Organisation' },
    { key: 'contactName', label: 'Contact' },
    { key: 'email', label: 'Email' },
    { key: 'status', label: 'Status' },
  ];

  return (
    <div className="obw-step-content">
      <h2 className="obw-title">Choose a subscriber</h2>
      <p className="obw-subtitle">
        Partner applications belong to an existing subscriber. Pick the subscriber you're onboarding
        as a partner — already-partner accounts are shown but can't be picked.
      </p>

      <div className="pick-controls">
        <input
          className="pick-search"
          type="search"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="Search organisation, contact, email, or customer №…"
          aria-label="Search subscribers"
        />
        <div className="pick-filters" role="group" aria-label="Filter">
          {([['eligible', 'Eligible only'], ['all', 'All subscribers'], ['partners', 'Already partners']] as const).map(
            ([key, label]) => (
              <button
                key={key}
                type="button"
                className={`pick-filter-btn${filter === key ? ' active' : ''}`}
                aria-pressed={filter === key}
                onClick={() => setFilter(key)}
              >
                {label}
              </button>
            ),
          )}
        </div>
      </div>

      {loadState === 'loading' && <p className="obw-subtitle">Loading subscribers…</p>}
      {loadState === 'error' && (
        <div className="portal-wizard-error" role="alert">Could not load subscribers. Please try again.</div>
      )}

      {loadState === 'loaded' && (
        <div className="pick-table-wrap">
          <table className="pick-table">
            <thead>
              <tr>
                {cols.map((c) => (
                  <th key={c.key} className="pick-th" aria-sort={ariaSort(c.key)} onClick={() => toggleSort(c.key)}>
                    {c.label}
                    <span className="pick-arr" aria-hidden="true">
                      {sort.key === c.key ? (sort.dir === 'asc' ? '▲' : '▼') : '↕'}
                    </span>
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {filtered.length === 0 && (
                <tr><td colSpan={4} className="pick-empty">No subscribers match.</td></tr>
              )}
              {filtered.map((r) => {
                const isSelected = selected?.accountId === r.accountId;
                return (
                  <tr
                    key={r.accountId}
                    className={`pick-row${r.isPartner ? ' pick-row-disabled' : ''}${isSelected ? ' pick-row-selected' : ''}`}
                    onClick={() => { if (!r.isPartner) setSelected(r); }}
                    aria-disabled={r.isPartner}
                  >
                    <td className="pick-org">
                      <span className="pick-org-name">{r.businessName || '—'}</span>
                      <span className="pick-cn">{r.customerNumber}</span>
                    </td>
                    <td>{r.contactName || '—'}</td>
                    <td className="pick-email">{r.email || '—'}</td>
                    <td>
                      {r.isPartner
                        ? <span className="pick-badge pick-badge-partner">Partner</span>
                        : r.hasInProgressApplication
                          ? <span className="pick-badge pick-badge-eligible" title="Has an application in progress — you'll be asked to continue or restart">In progress</span>
                          : <span className="pick-badge pick-badge-eligible">Eligible</span>}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {launchError && <div className="portal-wizard-error" role="alert">{launchError}</div>}

      <div className="pick-footer">
        <span className="pick-selected" aria-live="polite">
          {selected
            ? <>Selected: <strong>{selected.businessName || '—'}</strong> ({selected.customerNumber})</>
            : 'No subscriber selected'}
        </span>
        <button
          type="button"
          className="obw-btn-primary"
          onClick={() => void handleContinue()}
          disabled={!selected || selected.isPartner || launching}
        >
          {launching ? 'Starting…' : 'Continue →'}
        </button>
      </div>
    </div>
  );
}
